import { useEffect, useState } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { Map } from 'immutable';

import * as onboardingActions from 'reducers/user/actions';
import { UserFeatureFlags, UserMembershipFeatures } from 'enums/user-membership-features';
import * as teamActions from 'reducers/teams/actions';
import * as teamsWizzardActions from 'reducers/teamsWizzard/actions';
import { UserType } from 'enums/user-type';
import { getOnboardingFlow } from 'modules/Onboarding/flows';
import { makeSelectFirstByType } from 'reducers/fundingSearch/selectors';
import { makeSelectIsAuthenticated, makeSelectUserType } from 'reducers/auth/selectors';
import { makeSelectOnboardingType } from 'reducers/user/onboarding/selectors';
import {
  makeSelectMarketplaceApiKey,
  makeSelectUser,
  makeSelectUserCompanyId,
  makeSelectUserConsultant,
  makeSelectUserFeature,
  makeSelectUserTeam,
} from 'reducers/user/selectors';
import { makeSelectFeatureFlagActive } from 'reducers/featureFlags/selectors';

import OnboardingSession from 'utils/Onboarding';
import { Layout } from '@capitalise/ui-design-system';
import OnboardingWizzard from 'modules/Onboarding/components/Wizzard/Wizzard';
import OnboardingAlpha from './OnboardingAlpha';
import { QuestionaireContextProvider } from 'modules/Business/providers/questionaire';
import { QuestionnaireType } from 'enums/questionnaire-type';
import { Analytics } from 'utils/Analytics';
import { AnalyticsEventAction, AnalyticsEventCategory } from 'enums/analytics-types';

const newDesigns = [
  'onboardingBusiness',
  'onboardingBusinessSoleTrader',
  'onboardingBusinessZA',
  'onboardingFreeOwner',
  'onboardingFreeUser',
  'onboardingProUserInvited',
  'onboardingProUser',
  'onboardingProSponsor',
];

interface OnboardingProps {
  addNewTeam: Function;
  completeOnboarding: Function;
  creditReportDownloads: number | boolean;
  firstActiveSearch: any;
  goToRoute: Function;
  isAuthenticated: boolean;
  onboardingType: string;
  playbook: boolean;
  requestMyTeam: Function;
  userCompanyId: number;
  userConsultant: object;
  user: object;
  userType: string;
  capitalReport: boolean;
  hasNewDesignSystem: boolean;
  team: any;
  apiKey: string;
}

const Onboarding = (props: OnboardingProps) => {
  const {
    addNewTeam,
    completeOnboarding,
    creditReportDownloads,
    firstActiveSearch,
    goToRoute,
    isAuthenticated,
    onboardingType,
    playbook,
    requestMyTeam,
    userCompanyId,
    userConsultant,
    userType,
    user,
    capitalReport,
    hasNewDesignSystem,
    apiKey,
  } = props;

  const isAnonymousUser = localStorage.getItem('anonymous-user') === 'true';

  const [visible, setVisible] = useState(true);
  const [completed, setCompleted] = useState(false);

  const setBodyOverflow = (overflow) => {
    const { body } = document;

    if (body) {
      body.style.overflow = overflow ? 'hidden' : '';
    }
  };

  const skipOnboarding = () => {
    setVisible(false);
    OnboardingSession.skip();
    Analytics.track(AnalyticsEventCategory.onboard, AnalyticsEventAction.skip);
    setBodyOverflow(false);
  };

  useEffect(() => {
    if (isAnonymousUser) {
      skipOnboarding();

      localStorage.removeItem('anonymous-user');
    }
  }, [isAnonymousUser]);

  useEffect(() => {
    if (!completed) {
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      requestMyTeam();
    }
  }, [isAuthenticated, requestMyTeam]);

  const skippedOnboarding = !!OnboardingSession.hasSkipped();

  if (completed || !visible || skippedOnboarding || !onboardingType || !isAuthenticated) {
    return null;
  }

  const onCompleteOnboarding = () => {
    setCompleted(true);
    completeOnboarding(onboardingType);
    setBodyOverflow(false);
  };

  const defaultCompleteOnboarding = () => {
    onCompleteOnboarding();
    goToRoute('/dashboard');
    setBodyOverflow(false);
  };

  const flowConfig = getOnboardingFlow(onboardingType, {
    addNewTeam,
    companyId: userCompanyId,
    completeOnboarding: onCompleteOnboarding,
    consultant: userConsultant || Map(),
    creditReports: creditReportDownloads || 50,
    searchId: firstActiveSearch ? firstActiveSearch.get('id') : null,
    entityId: capitalReport ? userCompanyId : null,
    goToRoute,
    isIntroducer: userType === UserType.introducer,
    playbook,
    hasNewDesignSystem,
    user,
  });

  if (!flowConfig || flowConfig.length === 0) {
    return null;
  }

  if (!newDesigns.includes(onboardingType)) {
    setBodyOverflow(true);
  }

  return (
    <Layout>
      <QuestionaireContextProvider
        apiKey={apiKey}
        defaultType={QuestionnaireType.introducerWorkingCapital}
        onCloseCallback={onCompleteOnboarding}
      >
        {newDesigns.includes(onboardingType) ? (
          <OnboardingAlpha
            skipOnboarding={skipOnboarding}
            onCompleteOnboarding={onCompleteOnboarding}
            {...props}
          />
        ) : (
          <OnboardingWizzard
            completeOnboarding={defaultCompleteOnboarding}
            config={flowConfig}
            skipOnboarding={skipOnboarding}
          />
        )}
      </QuestionaireContextProvider>
    </Layout>
  );
};

const mapStateToProps = createStructuredSelector({
  creditReportDownloads: makeSelectUserFeature(UserMembershipFeatures.creditReportDownloads),
  capitalReport: makeSelectFeatureFlagActive(UserFeatureFlags.capitalReport),
  firstActiveSearch: makeSelectFirstByType('active'),
  isAuthenticated: makeSelectIsAuthenticated(),
  onboardingType: makeSelectOnboardingType(),
  playbook: makeSelectUserFeature(UserMembershipFeatures.learningLicense),
  userCompanyId: makeSelectUserCompanyId(),
  userConsultant: makeSelectUserConsultant(),
  userType: makeSelectUserType(),
  hasNewDesignSystem: makeSelectFeatureFlagActive(UserFeatureFlags.designSystemAlpha),
  team: makeSelectUserTeam(),
  apiKey: makeSelectMarketplaceApiKey(),
  user: makeSelectUser(),
});

const mapDispatchToProps = (dispatch) => ({
  addNewTeam: (startPath) => dispatch(teamsWizzardActions.openTeamsWizzard(startPath)),
  completeOnboarding: (type) => dispatch(onboardingActions.onboardingComplete(type)),
  goToRoute: (path) => dispatch(push(path)),
  requestMyTeam: (type) => dispatch(teamActions.requestMyTeam(type)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Onboarding);
